import PropTypes from 'prop-types';
import React from 'react';

import {
    IconArrowLeft,
    IconArrowRight,
    IconClose
} from '$partials/icons/index.js';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const JobItem = ({
    applicationClosingDate = null,
    authority = null,
    contractType = null,
    handleClose = null,
    handleNext = null,
    handlePrev = null,
    duration = null,
    location = null,
    locationRemote = null,
    mapModule = null,
    salaryMax = null,
    salaryMin = null,
    title = null,
    url = null
}) => {
    // render
    return (
        <>
            {mapModule && (
                <div className='relative'>
                    <div className='md:block py-40 md:py-30'>
                        <div className='pb-50'>
                            {(location && !locationRemote) && (
                                <p className='type-style-4 pb-30'>
                                    {location}
                                </p>
                            )}

                            {locationRemote && (
                                <p className='type-style-4 pb-30'>
                                    Remote
                                </p>
                            )}

                            <h3 className='type-style-3 pb-20'>
                                {title}
                            </h3>

                            {authority && (
                                <p className='type-style-4'>
                                    sought by {authority.title}
                                </p>
                            )}
                        </div>

                        <div className='flex gap-20'>
                            <div className='w-6/12'>
                                {contractType && (
                                    <p className='type-style-4 pb-10'>
                                        {contractType?.title}
                                    </p>
                                )}

                                {salaryMin === salaryMax ? (
                                    <p className='type-style-4'>
                                        {salaryMin && (
                                            <span>
                                                {new Intl.NumberFormat(
                                                    'en-GB',
                                                    {
                                                        currency: 'GBP',
                                                        maximumFractionDigits: 0,
                                                        minimumFractionDigits: 0,
                                                        style: 'currency',
                                                    }
                                                ).format(salaryMin)}
                                            </span>
                                        )}
                                    </p>
                                ) : (
                                    <p className='type-style-4'>
                                        {salaryMin && (
                                            <span>
                                                {new Intl.NumberFormat(
                                                    'en-GB',
                                                    {
                                                        currency: 'GBP',
                                                        maximumFractionDigits: 0,
                                                        minimumFractionDigits: 0,
                                                        style: 'currency',
                                                    }
                                                ).format(salaryMin)}
                                            </span>
                                        )}

                                        {salaryMin && salaryMax && (
                                            <span> &ndash; </span>
                                        )}

                                        {salaryMax && (
                                            <span>
                                                {new Intl.NumberFormat(
                                                    'en-GB',
                                                    {
                                                        currency: 'GBP',
                                                        maximumFractionDigits: 0,
                                                        minimumFractionDigits: 0,
                                                        style: 'currency',
                                                    }
                                                ).format(salaryMax)}
                                            </span>
                                        )}
                                    </p>
                                )}
                            </div>

                            <div className='w-6/12'>
                                {duration && (
                                    <p className='type-style-4 pb-10'>
                                        {duration?.title}
                                    </p>
                                )}

                                {applicationClosingDate && (
                                    <p className='type-style-4'>
                                        {new Intl.DateTimeFormat(
                                            'en-GB',
                                            {
                                                day: 'numeric',
                                                month: 'short',
                                                year: 'numeric'

                                            }).format(new Date(applicationClosingDate))}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className='absolute top-30 right-0'>
                        <button
                            className='ml-auto rounded-full'
                            onClick={handleClose}
                            type='button'>
                            <span className='sr-only'>
                                Close Tooltip
                            </span>

                            <IconClose />
                        </button>
                    </div>

                    <div className='flex justify-between items-center pb-30'>
                        <div>
                            <a
                                className='pill-button'
                                href={url}>
                                Find out more
                            </a>
                        </div>

                        <div className='flex gap-10'>
                            <button
                                className='map__next-button'
                                onClick={handlePrev}
                                type='button'>
                                <span className='sr-only'>
                                    Previous
                                </span>

                                <IconArrowLeft />
                            </button>

                            <button
                                className='map__next-button'
                                onClick={handleNext}
                                type='button'>
                                <span className='sr-only'>
                                    Next
                                </span>

                                <IconArrowRight />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {!mapModule && (
                <div className='layout-container border-t border-charcoal expand-interaction hover:bg-peach focus-within:bg-peach'>
                    <div className='layout-grid-negative-gutter'>
                        <div className='flex flex-wrap items-center py-40'>
                            <div className='w-12/12 sm:hidden layout-grid-col-padding'>
                                {(location && !locationRemote) && (
                                    <p className='type-style-4 pb-30'>
                                        {location}
                                    </p>
                                )}

                                {locationRemote && (
                                    <p className='type-style-4 pb-30'>
                                        Remote
                                    </p>
                                )}
                            </div>

                            <div className='hidden md:block md:w-1/12' />

                            <div className='sm:w-12/12 md:w-4/12 layout-grid-col-padding pb-60 sm:pb-40 md:pb-0'>
                                <h3 className='type-style-3 pb-20'>
                                    <a
                                        className='expand-interaction__action'
                                        href={url}>
                                        {title}
                                    </a>
                                </h3>

                                {authority && (
                                    <p className='type-style-4'>
                                        sought by {authority.title}
                                    </p>
                                )}
                            </div>

                            <div className='w-6/12 sm:w-4/12 md:w-2/12 layout-grid-col-padding'>
                                {contractType && (
                                    <p className='type-style-4 pb-10'>
                                        {contractType.title}
                                    </p>
                                )}

                                {salaryMin === salaryMax ? (
                                    <p className='type-style-4'>
                                        {salaryMin && (
                                            <span>
                                                {new Intl.NumberFormat(
                                                    'en-GB',
                                                    {
                                                        currency: 'GBP',
                                                        maximumFractionDigits: 0,
                                                        minimumFractionDigits: 0,
                                                        style: 'currency',
                                                    }
                                                ).format(salaryMin)}
                                            </span>
                                        )}
                                    </p>
                                ) : (
                                    <p className='type-style-4'>
                                        {salaryMin && (
                                            <span>
                                                {new Intl.NumberFormat(
                                                    'en-GB',
                                                    {
                                                        currency: 'GBP',
                                                        maximumFractionDigits: 0,
                                                        minimumFractionDigits: 0,
                                                        style: 'currency',
                                                    }
                                                ).format(salaryMin)}
                                            </span>
                                        )}

                                        {salaryMin && salaryMax && (
                                            <span> &ndash; </span>
                                        )}

                                        {salaryMax && (
                                            <span>
                                                {new Intl.NumberFormat(
                                                    'en-GB',
                                                    {
                                                        currency: 'GBP',
                                                        maximumFractionDigits: 0,
                                                        minimumFractionDigits: 0,
                                                        style: 'currency',
                                                    }
                                                ).format(salaryMax)}
                                            </span>
                                        )}
                                    </p>
                                )}
                            </div>

                            <div className='w-6/12 sm:w-4/12 md:w-2/12 layout-grid-col-padding'>
                                {duration && (
                                    <p className='type-style-4 pb-10'>
                                        {duration?.title}
                                    </p>
                                )}

                                {applicationClosingDate && (
                                    <p className='type-style-4'>
                                        {new Intl.DateTimeFormat(
                                            'en-GB',
                                            {
                                                day: 'numeric',
                                                month: 'short',
                                                year: 'numeric'

                                            }).format(new Date(applicationClosingDate))}
                                    </p>
                                )}
                            </div>

                            <div className='hidden sm:block sm:w-4/12 md:w-2/12 layout-grid-col-padding'>
                                {(location && !locationRemote) && (
                                    <p className='type-style-4'>
                                        {location}
                                    </p>
                                )}

                                {locationRemote && (
                                    <p className='type-style-4'>
                                        Remote
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    applicationClosingDate: PropTypes.string.isRequired,
    authority: PropTypes.object.isRequired,
    contractType: PropTypes.object.isRequired,
    handleClose: PropTypes.func,
    handleNext: PropTypes.func,
    handlePrev: PropTypes.func,
    duration: PropTypes.object.isRequired,
    location: PropTypes.string.isRequired,
    locationRemote: PropTypes.bool.isRequired,
    mapModule: PropTypes.bool,
    salaryMax: PropTypes.number.isRequired,
    salaryMin: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired
};

JobItem.propTypes = propTypes;

export default JobItem;
